import { isGroupTripDateConflictRetryable, opaqueId } from './groupTripModel';

/** 會擋住途中加入入口的同行者狀態。LEFT／REMOVED 不能重新加入。 */
const BLOCKING_TRAVELER_STATUSES = new Set(['ACTIVE', 'LEFT', 'REMOVED']);

const REQUEST_STATUS_KEYS = {
  PENDING: 'travel.groupTrip.lateJoinStatusPending',
  APPROVED: 'travel.groupTrip.lateJoinStatusApproved',
  REJECTED: 'travel.groupTrip.lateJoinStatusRejected',
  CANCELLED: 'travel.groupTrip.lateJoinStatusCancelled',
};

const FAILURE_KEYS = {
  NOT_TRAVELING: 'travel.groupTrip.lateJoinErrorNotTraveling',
  ALREADY_TRAVELER: 'travel.groupTrip.lateJoinErrorAlreadyTraveler',
  ALREADY_ON_TRIP: 'travel.groupTrip.lateJoinErrorAlreadyTraveler',
  REJOIN_NOT_SUPPORTED: 'travel.groupTrip.lateJoinErrorRejoin',
  HOST_CANNOT_REQUEST: 'travel.groupTrip.lateJoinErrorHostCannotRequest',
  DUPLICATE_PENDING_REQUEST: 'travel.groupTrip.lateJoinSamePending',
  REQUEST_NOT_PENDING: 'travel.groupTrip.lateJoinErrorNotPending',
  APPROVED_WITHOUT_TRAVELER: 'travel.groupTrip.lateJoinErrorApprovedWithoutTraveler',
  NOT_HOST: 'travel.groupTrip.lateJoinErrorNotHost',
  CAPACITY_FULL: 'travel.groupTrip.lateJoinCapacityFullBody',
  // 主揪核准晚加入時，申請人另有已確認的重疊揪團；不呈現對方的其他行程。
  GROUP_TRIP_DATE_CONFLICT: 'travel.groupTrip.dateConflict.applicantConflict',
  GROUP_TRIP_DATE_BUSY: 'travel.groupTrip.dateConflict.busy',
};

/**
 * 只在已知不是 Host、且自己沒有 ACTIVE／LEFT／REMOVED 同行者時顯示入口。
 * Journey TRAVELING 不在這份讀模型上，是否真的在旅途中由後端決定。
 */
export function canShowLateJoinEntry({ viewerId, hostMemberId, travelers }) {
  if (viewerId == null || viewerId === '') return false;
  if (hostMemberId != null && String(viewerId) === String(hostMemberId)) return false;
  const mine = (travelers || []).filter((row) => (
    row?.linkedMemberId != null && String(row.linkedMemberId) === String(viewerId)
  ));
  if (mine.some((row) => BLOCKING_TRAVELER_STATUSES.has(row.status))) return false;
  return true;
}

/**
 * 公開頁來源文案只依後端權威來源，不以時間或帳號綁定推測。
 * 未提供來源的舊回應採中性已加入文案。
 * 名單還沒回來時不猜，避免先畫出「已核准加入」。
 */
export function publicParticipantOriginLead({
  travelers,
  viewerId,
  rosterReady,
  t,
  statusLabel,
}) {
  if (!rosterReady || typeof t !== 'function') return '';
  const mine = (travelers || []).find((row) => (
    viewerId != null
    && row?.linkedMemberId != null
    && String(row.linkedMemberId) === String(viewerId)
  ));
  if (mine?.joinSource === 'REPLACEMENT') return `${t('travel.groupTrip.replacementJoined')}${statusLabel ? ` · ${statusLabel}` : ''}.`;
  if (mine?.joinSource !== 'NORMAL') return `${t('travel.groupTrip.joinedParticipant')}.`;
  const approved = t('travel.groupTrip.approvedParticipant');
  return `${approved}${statusLabel ? ` · ${statusLabel}` : ''}.`;
}

export function canShowHostLateJoinActions(viewerId, hostMemberId) {
  return viewerId != null
    && viewerId !== ''
    && hostMemberId != null
    && String(viewerId) === String(hostMemberId);
}

export function lateJoinRequestStatusLabel(status, t) {
  const key = REQUEST_STATUS_KEYS[status];
  return key ? t(key) : '';
}

export function normalizeLateJoinRequest(value) {
  return {
    requestId: opaqueId(value?.requestId),
    groupTripId: opaqueId(value?.groupTripId),
    requesterMemberId: opaqueId(value?.requesterMemberId),
    status: value?.status || '',
    materializedTravelerId: opaqueId(value?.materializedTravelerId),
    createTime: value?.createTime || '',
    updateTime: value?.updateTime || '',
    effectiveJoinAt: value?.effectiveJoinAt || null,
  };
}

/** 保留後端 createTime DESC, id DESC，不在客戶端重排。 */
export function normalizeLateJoinRequestList(payload) {
  const source = Array.isArray(payload?.items) ? payload.items : [];
  return source
    .map(normalizeLateJoinRequest)
    .filter((item) => item.requestId != null);
}

export function normalizeLateJoinHostQueue(payload) {
  return {
    capacity: Number.isFinite(Number(payload?.capacity)) ? Number(payload.capacity) : null,
    occupied: Number.isFinite(Number(payload?.occupied)) ? Number(payload.occupied) : null,
    items: normalizeLateJoinRequestList(payload),
  };
}

/** 同一筆 PENDING 只留一張卡。 */
export function reconcileLateJoinHistory(items, returnedRequest) {
  const next = Array.isArray(items) ? items.slice() : [];
  if (!returnedRequest?.requestId) return next;
  const index = next.findIndex((item) => String(item.requestId) === String(returnedRequest.requestId));
  if (index >= 0) {
    next[index] = { ...next[index], ...returnedRequest };
    return next;
  }
  if (returnedRequest.status === 'PENDING' && next.some((item) => item.status === 'PENDING')) {
    return next;
  }
  return [returnedRequest, ...next];
}

export function lateJoinFailureCode(cause) {
  const key = String(cause?.raw?.key || cause?.key || '');
  const message = String(cause?.raw?.message || cause?.message || '');
  if (key.includes('capacity.exceeded') || message.includes('capacity exceeded')) return 'CAPACITY_FULL';
  if (key === 'travel.grouptrip.date.conflict_other') return 'GROUP_TRIP_DATE_CONFLICT';
  if (key === 'travel.grouptrip.date.check_busy') return 'GROUP_TRIP_DATE_BUSY';
  if (key === 'travel.grouptrip.date.conflict') {
    return isGroupTripDateConflictRetryable(cause) ? 'GROUP_TRIP_DATE_BUSY' : 'GROUP_TRIP_DATE_CONFLICT';
  }
  const known = Object.keys(FAILURE_KEYS);
  return known.find((code) => message.includes(code) || key.includes(code)) || '';
}

export function lateJoinFailureMessage(cause, t) {
  const code = lateJoinFailureCode(cause);
  const key = FAILURE_KEYS[code];
  return key ? t(key) : '';
}

/** 伺服器 LocalDateTime，不經時區換算，也不能改。 */
export function formatEffectiveJoinAt(value) {
  if (value == null || value === '') return '';
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return text;
  return `${Number(match[2])}/${Number(match[3])} ${match[4]}:${match[5]}`;
}

export function lateJoinTravelerNote(traveler, tf) {
  if (traveler?.joinSource === 'REPLACEMENT') return tf('travel.groupTrip.replacementJoined', {});
  if (traveler?.joinSource !== 'MID_TRIP') return '';
  if (!traveler?.effectiveJoinAt) return tf('travel.groupTrip.midTripJoined', {});
  const when = formatEffectiveJoinAt(traveler.effectiveJoinAt);
  if (!when) return '';
  return tf('travel.groupTrip.lateJoinTravelerContext', { when });
}

/**
 * 四個 D2 事件。LATE_JOIN_APPROVED 不當成產品事件。
 * 取消文案依 actor 是否為主辦人分開，不一律寫申請人取消。
 */
export function lateJoinTimelineLabel(item, t, hostMemberId) {
  const type = item?.eventType;
  if (type === 'LATE_JOIN_REQUESTED') return t('travel.groupTrip.lateJoinTimelineRequested');
  if (type === 'LATE_JOIN_REQUEST_CANCELLED') {
    const byHost = hostMemberId != null && String(item?.actorMemberId) === String(hostMemberId);
    return t(byHost
      ? 'travel.groupTrip.lateJoinTimelineCancelledByHost'
      : 'travel.groupTrip.lateJoinTimelineCancelledByRequester');
  }
  if (type === 'LATE_JOIN_REJECTED') return t('travel.groupTrip.lateJoinTimelineRejected');
  if (type === 'TRAVELER_JOINED_LATE') return t('travel.groupTrip.lateJoinTimelineJoined');
  if (type === 'LATE_JOIN_APPROVED') return '';
  return null;
}
